/**
 * Which corners of a wall's elevation outline are its TOP edge and which its
 * BOTTOM edge — read from the outline's SHAPE, not from how high a corner is.
 *
 * A wall outline is a ring in the wall's own (along, height) plane. Its two
 * ends are near-vertical edges — the ones furthest along the wall in each
 * direction — and they break the ring into two arcs: one runs over the top,
 * one along the bottom, and the higher of the two is the top.
 *
 * The rule this replaces said "a corner more than 10 cm above the wall's
 * lowest corner is a top corner". That holds only while the bottom is FLAT. A
 * wall stepped down behind a sunken room (Step 3.5c, then Step 6's
 * min-of-sides) has bottom corners 0.4 m above its lowest one, and the height
 * rule reads them as the top: Step 7's corner snap grabbed a bottom corner as
 * the wall's top end, and the exterior inherited bottom corners into its top
 * edge. `functions/versions.js` 2.158.0 named it — "TOP_EDGE_CUT remains a
 * latent weakness for any source wall with a non-trivial bottom edge". This
 * walk was already how Step 6 read its contributors
 * (`structure-wall-aggregation.js`); it lives here so every reader of an
 * outline uses the one copy.
 *
 * Pure.
 */

/**
 * How far out of plumb a SIDE edge may run, along the wall, and still be
 * recognised as a side.
 *
 * There is deliberately no minimum HEIGHT to go with it. The old rule wanted
 * 5 cm of rise, and a gable's rake foot lands on the wall base by construction,
 * so its side edge is a few centimetres or nothing: `D7155424`'s Loft walls
 * have a 4 cm side and a 1 cm one, which left one side detected, no split, and
 * a fallback that kept only the collar. Height says nothing about whether an
 * edge is a side; along-axis position does.
 *
 * 1 cm was too tight in the other direction — scan walls are routinely a couple
 * of centimetres out of plumb (`1FE84786`'s living room: 0.45 against 0.47).
 */
const SIDE_EDGE_DT_TOL = 0.05;

/**
 * The height rule, kept as the FALLBACK for an outline whose sides cannot be
 * found (a triangle, slanted ends): above the lowest corner by this much is
 * the top.
 */
const TOP_EDGE_CUT_M = 0.1;

const { MIN_REAL_STEP_M } = require('./floorYReconciliation');

/** Distance of each corner along `axis` ({ origin: {x, z}, ux, uz }). */
function alongAxis(corners, axis) {
  return corners.map(
    (c) => (c.x - axis.origin.x) * axis.ux + (c.z - axis.origin.z) * axis.uz
  );
}

/**
 * Split an outline into its top and bottom arcs by its two side edges.
 *
 * Each arc runs between the two sides in RING order, so a vertical step in
 * either edge stays as two consecutive corners at one position — which is
 * what lets a caller rebuild the step rather than slope across it.
 *
 * @param {Array<{x:number,y:number,z:number}>} corners
 * @param {{origin:{x:number,z:number}, ux:number, uz:number}} axis
 * @returns {{top:number[], bottom:number[]}|null} corner indices, or null
 *   when the outline does not have two distinguishable side edges.
 */
function splitOutlineEdges(corners, axis) {
  const N = (corners || []).length;
  if (N < 4) return null;
  const ts = alongAxis(corners, axis);
  // A side is at an END of the outline. A short stretch of the top inside it
  // is not one: D7155424's Loft gables carry a 1 cm kink up each rake, and
  // once the rake reached the deck at a point (`seatRakeEndWalls`) the kink
  // was the outermost "side" and the rake below it was read as bottom.
  const tLo = Math.min(...ts);
  const tHi = Math.max(...ts);
  const atEnd = (t) => t - tLo < SIDE_EDGE_DT_TOL || tHi - t < SIDE_EDGE_DT_TOL;
  const vertEdges = [];
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N;
    const dt = Math.abs(ts[j] - ts[i]);
    const dy = Math.abs(corners[j].y - corners[i].y);
    if (dt < SIDE_EDGE_DT_TOL && dy > 0 && atEnd(ts[i]) && atEnd(ts[j])) {
      vertEdges.push({ i, t: (ts[i] + ts[j]) / 2 });
    }
  }
  if (vertEdges.length < 2) return null;
  let minVE = vertEdges[0];
  let maxVE = vertEdges[0];
  for (const e of vertEdges) {
    if (e.t < minVE.t) minVE = e;
    if (e.t > maxVE.t) maxVE = e;
  }
  if (minVE === maxVE) return null;
  // Each side edge breaks the ring into two arcs. Arc A runs from the corner
  // after minVE through the corner AT the end of maxVE; arc B is the rest.
  const arcA = [];
  const arcB = [];
  let k = (minVE.i + 1) % N;
  while (k !== (maxVE.i + 1) % N) {
    arcA.push(k);
    k = (k + 1) % N;
  }
  k = (maxVE.i + 1) % N;
  while (k !== (minVE.i + 1) % N) {
    arcB.push(k);
    k = (k + 1) % N;
  }
  if (arcA.length === 0 || arcB.length === 0) return null;
  const avgY = (arc) =>
    arc.reduce((s, idx) => s + corners[idx].y, 0) / arc.length;
  const aIsTop = avgY(arcA) > avgY(arcB);
  return { top: aIsTop ? arcA : arcB, bottom: aIsTop ? arcB : arcA };
}

/**
 * Top and bottom corners for the readers that used the height rule (Steps 7
 * and 8): by SHAPE where the bottom is a real step (`isSteppedSplit`), which
 * is the case the height rule misreads, and by the height rule everywhere
 * else — so every other wall is classified exactly as it always was. Reading
 * every wall by shape moved a gable's rake foot on `D7155424` and dropped the
 * stair from `EC3C3390`'s exterior top; neither wall has a stepped bottom.
 *
 * @returns {{top:number[], bottom:number[], byShape:boolean}}
 */
function outlineEdges(corners, axis) {
  const split = splitOutlineEdges(corners, axis);
  if (isSteppedSplit(corners, axis, split)) return { ...split, byShape: true };
  const pc = corners || [];
  const minY = Math.min(...pc.map((c) => c.y));
  const top = [];
  const bottom = [];
  pc.forEach((c, i) => (c.y > minY + TOP_EDGE_CUT_M ? top : bottom).push(i));
  return { top, bottom, byShape: false };
}

/**
 * The height of one edge (`indices`, in ring order) at distance `t` along
 * `axis`, interpolated along the edge and held flat past its ends. A vertical
 * step is never read as a slope: only a segment that has length along the
 * wall can answer, and at a step's own position the stretch on the side `t`
 * is approached from — `side` -1 or +1 — wins.
 *
 * @returns {number|null}
 */
function edgeYAt(corners, indices, axis, t, side = 0) {
  if (!indices?.length) return null;
  const pts = indices.map((i) => corners[i]);
  const ts = alongAxis(pts, axis);
  let lo = 0;
  let hi = 0;
  for (let i = 1; i < ts.length; i++) {
    if (ts[i] < ts[lo]) lo = i;
    if (ts[i] > ts[hi]) hi = i;
  }
  if (t <= ts[lo]) return pts[lo].y;
  if (t >= ts[hi]) return pts[hi].y;
  const probe = t + side * 1e-6;
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = ts[i];
    const b = ts[i + 1];
    const span = b - a;
    if (Math.abs(span) < 1e-9) continue;
    const u = (probe - a) / span;
    if (u < -1e-9 || u > 1 + 1e-9) continue;
    return pts[i].y + Math.min(1, Math.max(0, u)) * (pts[i + 1].y - pts[i].y);
  }
  return null;
}

/**
 * Whether a shape split's bottom is STEPPED — running from one side to the
 * other without doubling back along the wall, and dropping by at least a real
 * step (`MIN_REAL_STEP_M`) somewhere. False for nearly every wall.
 *
 * Both conditions came from walls the shape reading got wrong. A multi-scan
 * merge leaves a few centimetres' notch where two captures' floors disagree
 * (`christy-manual`: 5.4 cm), which is registration, not a floor. And a stair
 * wall's outline can turn back on itself (`EC3C3390`), so the shape walk's
 * arcs there run up and down the stair; read by shape, the exterior lost the
 * stair from its top.
 */
function isSteppedSplit(corners, axis, split) {
  if (!split || split.bottom.length < 2) return false;
  const ys = split.bottom.map((i) => corners[i].y);
  if (Math.max(...ys) - Math.min(...ys) < MIN_REAL_STEP_M) return false;
  const ts = alongAxis(
    split.bottom.map((i) => corners[i]),
    axis
  );
  const dir = Math.sign(ts[ts.length - 1] - ts[0]);
  if (dir === 0) return false;
  for (let i = 1; i < ts.length; i++) {
    if ((ts[i] - ts[i - 1]) * dir < -1e-3) return false;
  }
  return true;
}

/**
 * The bottom corner indices, in ring order, of an outline whose bottom is a
 * real step (`isSteppedSplit`); null otherwise — the bottom is then flat at
 * its lowest point, as it always was.
 */
function steppedBottom(corners, axis) {
  const split = splitOutlineEdges(corners, axis);
  return isSteppedSplit(corners, axis, split) ? split.bottom : null;
}

module.exports = {
  splitOutlineEdges,
  outlineEdges,
  steppedBottom,
  edgeYAt,
  alongAxis,
  SIDE_EDGE_DT_TOL,
  TOP_EDGE_CUT_M,
};
